/**
 * Edit Product V3 - Types
 *
 * V3-specific types for the schema-driven product editor.
 * These types extend the base ProductV2 model with form-specific concerns.
 */

import type {
  ProductV2,
  ProductAxes,
  ProductVisualization,
  ProductCommerce,
  ProductPricing,
  SizeAxis,
  ColorAxis,
  VisualizationType,
} from '../../../../common/models/product-v2.model';
import type {
  VariantAxisConfig,
  FamilyAttribute,
  ProductFamily,
} from '../../../../common/models/product-family.model';

// =============================================================================
// Field Path Constants
// =============================================================================

/**
 * Fields stored at root level (not in attributes object)
 * These are basic product info fields with dedicated UI
 */
export const BASIC_INFO_FIELDS = ['name', 'brand', 'description'] as const;

/**
 * Fields stored in commerce/organization objects (not in attributes)
 * These have dedicated UI in OrganizationCard
 */
export const ORGANIZATION_FIELDS = ['link', 'addToCartLink', 'collections', 'applicationTypes', 'type'] as const;

/**
 * Fields that are variant-level, not product-level
 */
export const VARIANT_LEVEL_FIELDS = ['sku'] as const;

/**
 * All fields that should be excluded from dynamic attributes rendering
 * These fields have dedicated UI components and are not stored in attributes object
 */
export const SPECIAL_ATTRIBUTE_FIELDS = [
  ...BASIC_INFO_FIELDS,
  ...ORGANIZATION_FIELDS,
  ...VARIANT_LEVEL_FIELDS,
] as const;

// =============================================================================
// Form State Types
// =============================================================================

/**
 * V3 Form State - Mirrors ProductV2 exactly with form-only fields
 * No transformation needed between form and API
 */
export interface ProductFormStateV3 extends Omit<ProductV2, '_id' | 'createdAt' | 'updatedAt'> {
  /** Track if form has unsaved changes */
  _isDirty: boolean;
  /** JSON snapshot of original state for deep comparison */
  _originalSnapshot: string;
  /** Validation errors by field path */
  _validationErrors?: Record<string, string[]>;
}

/**
 * Form field path - dot notation for nested fields
 * @example "name", "commerce.link", "axes.color.value"
 */
export type FieldPath = string;

/**
 * Form field setter function type
 */
export type FieldSetter<T = unknown> = (path: FieldPath, value: T) => void;

// =============================================================================
// Axis Value Types (Unified)
// =============================================================================

/**
 * Dimension value for size axis
 */
export interface DimensionValue {
  width: number;
  height: number;
  depth?: number;
  unit: string;
}

/**
 * Unified axis value type - works for all axis types
 */
export type AxisValue = string | number | DimensionValue | ColorAxisValue | null;

/**
 * Default axis value used for empty optional axes in cartesian product.
 * Ensures every variant's `axes` record includes all enabled axes.
 * Already handled by existing guards (generateVariantKeyFromAxes, VariantMatrix, variantToProductPayload).
 */
export const AXIS_DEFAULT_VALUE: AxisValue = null;

/**
 * Check if an axis value is the default placeholder (used for empty optional axes)
 */
export function isDefaultAxisValue(value: AxisValue): boolean {
  return value === null;
}

/**
 * Color axis value (simplified for form use)
 */
export interface ColorAxisValue {
  value: string;
  hex?: string;
}

/**
 * Type guard: Check if value is a DimensionValue
 */
export function isDimensionValue(value: AxisValue): value is DimensionValue {
  return (
    value !== null &&
    typeof value === 'object' &&
    'width' in value &&
    'height' in value &&
    'unit' in value
  );
}

/**
 * Type guard: Check if value is a ColorAxisValue
 */
export function isColorAxisValue(value: AxisValue): value is ColorAxisValue {
  return (
    value !== null &&
    typeof value === 'object' &&
    'value' in value &&
    typeof (value as ColorAxisValue).value === 'string'
  );
}

/**
 * Type guard: Check if value is a string axis
 */
export function isStringAxisValue(value: AxisValue): value is string {
  return typeof value === 'string';
}

/**
 * Type guard: Check if value is a number axis
 */
export function isNumberAxisValue(value: AxisValue): value is number {
  return typeof value === 'number';
}

// =============================================================================
// Variant Types
// =============================================================================

/**
 * Variant in V3 - uses axes map instead of flat fields
 * Represents a single product variant in the form
 */
export interface VariantV3 {
  /** Unique variant UUID */
  uuid: string;
  /** Axes values map: { color: "Oak", size: {w,h,d,u}, pattern: "herringbone" } */
  axes: Record<string, AxisValue>;
  /** Stock keeping unit */
  sku?: string;
  /** Whether this variant is selected in the UI */
  isSelected: boolean;
  /** Whether this is the default variant */
  isDefault: boolean;
  /** Visualization configuration */
  visualization: ProductVisualization;
  /**
   * Contextual / lifestyle image URLs already hosted on our CDN (from a prior
   * save or from an API integration sync). Newly picked files live in
   * `_galleryUploads` until the next submission uploads them.
   */
  galleryImages?: string[];
  /** Pending gallery image file uploads (client-side only, sent as multipart) */
  _galleryUploads?: File[];
  /** Commerce links, and the variant's stored price / pack / sample */
  commerce: ProductCommerce;
  /**
   * What the API resolved for shoppers when this variant was loaded (price,
   * pack, sample and where each came from). READ-ONLY: not part of the dirty
   * snapshot and never sent in a payload.
   */
  pricing?: ProductPricing;
  /** Display order among sibling variants (lower = first) */
  siblingOrder?: number;
  /** Whether this variant has unsaved changes */
  _isDirty?: boolean;
  /** Whether this is a newly created variant (not yet saved) */
  _isNew?: boolean;
  /** Original data for comparison (JSON string) */
  _originalSnapshot?: string;
  /** Error info when this variant failed during bulk submission */
  _error?: {
    message: string;
    details?: Record<string, any>;
  };
}

/**
 * Grouped variants for table display
 * Groups by all axes EXCEPT the "innermost" one (typically pattern)
 */
export interface VariantGroupV3 {
  /** Unique key for this group (derived from groupAxes) */
  groupKey: string;
  /** Axes that define this group */
  groupAxes: Record<string, AxisValue>;
  /** Variants within this group */
  variants: VariantV3[];
  /** Which axis varies within this group */
  expandAxis: string;
  /** Whether this group is expanded in UI */
  isExpanded?: boolean;
}

/**
 * Axis values collection - all values for each axis
 * Used for variant generation
 */
export type AxisValuesMap = Record<string, AxisValue[]>;

// =============================================================================
// Card Configuration Types
// =============================================================================

/**
 * Card category for grouping fields
 */
export type CardCategory = 'identity' | 'organization' | 'commerce' | 'family_attributes' | 'custom';

/**
 * Card configuration derived from family
 */
export interface CardConfig {
  /** Unique card identifier */
  id: string;
  /** Card title */
  title: string;
  /** Card category */
  category: CardCategory;
  /** Fields to render in this card */
  fields: FamilyAttribute[];
  /** Whether the card can be collapsed */
  collapsible: boolean;
  /** Whether the card is expanded by default */
  defaultExpanded: boolean;
  /** Display order */
  order: number;
  /** Optional description/help text */
  description?: string;
  /** Whether this card is visible (based on family config) */
  isVisible?: boolean;
}

/**
 * All cards configuration for a product form
 */
export interface FormCardsConfig {
  identity: CardConfig;
  organization?: CardConfig;
  commerce: CardConfig;
  familyAttributes?: CardConfig;
  customCards: CardConfig[];
}

// =============================================================================
// Validation Types
// =============================================================================

/**
 * Validation severity levels
 */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * Single validation message
 */
export interface ValidationMessage {
  /** Field path that has the issue */
  field: FieldPath;
  /** Human-readable error message */
  message: string;
  /** Severity level */
  severity: ValidationSeverity;
  /** Validation rule that triggered this (for debugging) */
  rule?: string;
}

/**
 * Validation result for the entire form
 */
export interface ValidationResultV3 {
  /** Whether the form is valid (no errors) */
  isValid: boolean;
  /** All validation messages */
  messages: ValidationMessage[];
  /** Errors grouped by field path */
  errorsByField: Record<FieldPath, string[]>;
  /** Errors grouped by card/section */
  errorsByCard: Record<string, string[]>;
  /** Count of errors */
  errorCount: number;
  /** Count of warnings */
  warningCount: number;
}

/**
 * Validation rule definition
 */
export interface ValidationRule {
  /** Rule identifier */
  id: string;
  /** Field path this rule applies to (supports wildcards) */
  field: FieldPath;
  /** Validation function */
  validate: (value: unknown, context: ValidationContext) => boolean;
  /** Error message (can be function for dynamic messages) */
  message: string | ((value: unknown, context: ValidationContext) => string);
  /** Severity */
  severity: ValidationSeverity;
  /** Whether this rule is active */
  enabled?: boolean;
}

/**
 * Context passed to validation functions
 */
export interface ValidationContext {
  /** Full form state */
  formState: ProductFormStateV3;
  /** Product family configuration */
  family: ProductFamily;
  /** Field being validated */
  field: FieldPath;
  /** All field values */
  values: Record<string, unknown>;
}

// =============================================================================
// Submission Types
// =============================================================================

/**
 * Submission status
 */
export type SubmissionStatus = 'idle' | 'validating' | 'submitting' | 'success' | 'error';

/**
 * Submission progress for batch operations
 */
export interface SubmissionProgress {
  /** Current status */
  status: SubmissionStatus;
  /** Total items to process */
  total: number;
  /** Items processed so far */
  processed: number;
  /** Successfully processed items */
  succeeded: number;
  /** Failed items */
  failed: number;
  /** Current item being processed */
  currentItem?: string;
  /** Error message if status is 'error' */
  error?: string;
  /** Batch ID for async operations */
  batchId?: string;
  /** Per-job status from batch polling */
  jobs?: Array<{
    jobId: string;
    uuid?: string;
    operation?: string;
    status: string;
    error?: string;
    productData?: Record<string, any>;
    resultProduct?: Record<string, any>;
    errorDetails?: Record<string, any>;
  }>;
}

/**
 * Submission result
 */
export interface SubmissionResult {
  /** Whether submission was successful */
  success: boolean;
  /** Created/updated product IDs */
  productIds: string[];
  /** Any errors that occurred */
  errors: Array<{
    uuid?: string;
    message: string;
  }>;
  /** Batch ID for tracking async operations */
  batchId?: string;
  /** Per-job results from batch completion (avoids stale closure issues) */
  jobs?: SubmissionProgress['jobs'];
}

// =============================================================================
// Hook Return Types
// =============================================================================

/**
 * useProductFormV3 hook return type
 */
export interface UseProductFormV3Return {
  // State
  product: ProductFormStateV3 | null;
  isLoading: boolean;
  isEditMode: boolean;
  error: string | null;

  // Computed
  isDirty: boolean;
  variants: VariantV3[];
  variantGroups: VariantGroupV3[];

  // Original variant UUIDs for detecting deletions during submission
  originalVariantUuids: Set<string>;

  // Generic field setter
  setField: <T>(path: FieldPath, value: T) => void;

  // Axes
  setAxisValues: (axisName: string, values: AxisValue[]) => void;
  getAxisValues: (axisName: string) => AxisValue[];
  getAllAxisValues: () => AxisValuesMap;

  // Variants
  regenerateVariants: () => void;
  assignAxisValueToVariants: (axisName: string, value: AxisValue) => void;
  setVariantField: (uuid: string, field: string, value: unknown) => void;
  deleteVariant: (uuid: string) => void;
  setDefaultVariant: (uuid: string) => void;
  selectVariant: (uuid: string, selected: boolean) => void;
  selectAllVariants: (selected: boolean) => void;
  reorderVariants: (activeId: string, overId: string) => void;

  // Visualization
  setVisualizationField: (uuid: string, field: string, value: unknown) => void;

  // Variant patcher (gallery images, etc.)
  patchVariant: (uuid: string, patch: Partial<VariantV3>) => void;

  // Form actions
  reset: () => void;
  reload: (silent?: boolean, failedVariants?: VariantV3[]) => Promise<void>;
  appendVariants: (variants: VariantV3[]) => void;
}

/**
 * useProductValidationV3 hook return type
 */
export interface UseProductValidationV3Return {
  /** Current validation result */
  result: ValidationResultV3;
  /** Validate the entire form */
  validate: () => ValidationResultV3;
  /** Validate a specific field */
  validateField: (field: FieldPath) => ValidationMessage[];
  /** Clear all validation errors */
  clearErrors: () => void;
  /** Check if a specific field has errors */
  hasError: (field: FieldPath) => boolean;
  /** Get errors for a specific field */
  getFieldErrors: (field: FieldPath) => string[];
}

/**
 * useProductSubmissionV3 hook return type
 */
export interface UseProductSubmissionV3Return {
  /** Current submission progress */
  progress: SubmissionProgress;
  /**
   * Submit the form. Pass `{ publishDraft: true }` to also flip the draft
   * flag off (and make the product visible) when saving a previously-synced
   * draft product. Default is a normal save that preserves draft state.
   */
  submit: (options?: { publishDraft?: boolean }) => Promise<SubmissionResult>;
  /** Cancel ongoing submission */
  cancel: () => void;
  /** Reset submission state */
  reset: () => void;
  /** Set (replace) a pending file for a variant+type combination */
  setPendingFile: (upload: { file: File; type: 'thumbnail' | 'material' | 'plank' | 'model' | 'originalImage' | 'usdz'; variantUuid: string; metadata?: Record<string, unknown> }) => void;
  /** A picked file not uploaded yet (files are only sent on save), e.g. a 3D model to preview */
  getPendingFile: (variantUuid: string, type: 'thumbnail' | 'material' | 'plank' | 'model' | 'originalImage' | 'usdz') => File | undefined;
}

// =============================================================================
// Event Types
// =============================================================================

/**
 * Form change event
 */
export interface FormChangeEvent {
  field: FieldPath;
  value: unknown;
  previousValue: unknown;
}

/**
 * Variant change event
 */
export interface VariantChangeEvent {
  uuid: string;
  field: string;
  value: unknown;
  previousValue: unknown;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Create an empty form state
 */
export function createEmptyFormState(
  vendor: string,
  familyId?: string,
  familySlug?: string
): ProductFormStateV3 {
  const state: ProductFormStateV3 = {
    id: '',
    uuid: '',
    vendor,
    familyId,
    familySlug,
    name: '',
    brand: '',
    description: '',
    sku: '',
    axes: {},
    visualization: {
      type: 'surface',
      thumbnailPath: '',
      surface: {
        materialPath: '',
        materialPaths: [],
        grout: { color: '#00000000', width: 0 },
        seamlessParams: {},
      },
    },
    attributes: {},
    organization: {
      productType: '',
      applicationTypes: [],
    },
    commerce: {
      link: '',
      addToCartLink: '',
    },
    isDefault: false,
    isVisible: true,
    active: true,
    siblings: [],
    _isDirty: false,
    _originalSnapshot: '',
  };

  state._originalSnapshot = JSON.stringify(state);
  return state;
}

/**
 * Create an empty validation result
 */
export function createEmptyValidationResult(): ValidationResultV3 {
  return {
    isValid: true,
    messages: [],
    errorsByField: {},
    errorsByCard: {},
    errorCount: 0,
    warningCount: 0,
  };
}

/**
 * Create default submission progress
 */
export function createDefaultSubmissionProgress(): SubmissionProgress {
  return {
    status: 'idle',
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
  };
}

/**
 * Convert SizeAxis to DimensionValue (for form use)
 */
export function sizeAxisToDimensionValue(size: SizeAxis): DimensionValue {
  return {
    width: size.width,
    height: size.height,
    depth: size.depth,
    unit: size.unit,
  };
}

/**
 * Convert DimensionValue to SizeAxis (for API)
 */
export function dimensionValueToSizeAxis(dim: DimensionValue): SizeAxis {
  return {
    width: dim.width,
    height: dim.height,
    depth: dim.depth,
    unit: dim.unit,
  };
}

/**
 * Convert ColorAxis to ColorAxisValue (for form use)
 */
export function colorAxisToColorAxisValue(color: ColorAxis): ColorAxisValue {
  return {
    value: color.value,
    hex: color.hex,
  };
}

/**
 * Convert ColorAxisValue to ColorAxis (for API)
 */
export function colorAxisValueToColorAxis(color: ColorAxisValue): ColorAxis {
  return {
    value: color.value,
    hex: color.hex,
  };
}

/**
 * Generate a display string for an axis value
 */
export function formatAxisValueForDisplay(value: AxisValue): string {
  if (value === null || value === undefined) return '';

  if (isStringAxisValue(value)) return value;
  if (isNumberAxisValue(value)) return String(value);

  if (isDimensionValue(value)) {
    if (value.depth != null) {
      return `${value.width} × ${value.height} × ${value.depth} ${value.unit}`;
    }
    return `${value.width} × ${value.height} ${value.unit}`;
  }

  if (isColorAxisValue(value)) {
    return value.value;
  }

  return String(value);
}

/**
 * Generate a unique key for a variant based on its axes.
 * Tolerates undefined/null axes (e.g. draft products created by an API
 * integration sync, which have no axes until the vendor fills them in).
 */
export function generateVariantKeyFromAxes(
  axes: Record<string, AxisValue> | null | undefined,
): string {
  if (!axes) return '';
  const parts: string[] = [];

  for (const [key, value] of Object.entries(axes)) {
    if (value !== null && value !== undefined) {
      parts.push(`${key}:${formatAxisValueForDisplay(value)}`);
    }
  }

  return parts.sort().join('|');
}

/**
 * Check if two axis values are equal
 */
export function axisValuesAreEqual(a: AxisValue, b: AxisValue): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;

  if (typeof a !== typeof b) return false;

  if (isDimensionValue(a) && isDimensionValue(b)) {
    return (
      a.width === b.width &&
      a.height === b.height &&
      a.depth === b.depth &&
      a.unit === b.unit
    );
  }

  if (isColorAxisValue(a) && isColorAxisValue(b)) {
    return a.value === b.value && a.hex === b.hex;
  }

  return a === b;
}

// =============================================================================
// Re-exports from ProductV2 model for convenience
// =============================================================================

export type {
  ProductV2,
  ProductAxes,
  ProductVisualization,
  ProductCommerce,
  ProductOrganization,
  SizeAxis,
  ColorAxis,
  VisualizationType,
  SurfaceVisualization,
  Model3DVisualization,
  GroutSettings,
  SeamlessParams,
} from '../../../../common/models/product-v2.model';

export type {
  ProductFamily,
  FamilyAttribute,
  VariantAxisConfig,
  VisualizationConfig,
  AttributeFieldType,
} from '../../../../common/models/product-family.model';
